/**
 * Warehouse Stock Reservations API Client
 */
import { apiFetch } from '../apiFetch'

export type ReservationStatus = 'ACTIVE' | 'CONSUMED' | 'RELEASED'

export type ReservationItem = {
  id: string
  material_id: string
  material_code: string | null
  material_name: string
  qty_reserved_base: number
  qty_consumed_base: number
  remaining: number
  base_uom: string
  status: ReservationStatus
  requirement_line_id: string
  created_by: string
  created_at: string
  updated_at: string
  released_at: string | null
  released_by: string | null
  release_reason: string | null
  note: string | null

  batch_id: string | null
  batch_title: string | null
  demand_type: string | null

  plan_line_id: string | null
  plan_line_code: string | null
  order_code: string | null
  customer_name: string | null
  customer_po: string | null
  product_code: string | null
  product_name: string | null
  planned_qty: number | null
  plan_line_done: boolean | null

  makeup_request_id: string | null
  makeup_reason: string | null
  makeup_status: string | null

  last_change_at?: string | null
  last_change_by?: string | null
  last_change_reason?: string | null
}

export type ReservationListParams = {
  materialId?: string
  status?: string
  source?: string
  completed_lsx?: boolean
  planLineId?: string
  q?: string
  page?: number
  pageSize?: number
}

export type ReservationListResponse = {
  ok: boolean
  data: ReservationItem[]
  items: ReservationItem[]
  total: number
  page: number
  pageSize: number
  status_counts?: {
    active: number
    consumed: number
    released: number
    total: number
  }
}

export async function fetchReservationsApi(
  params: ReservationListParams = {},
): Promise<ReservationListResponse> {
  const query = new URLSearchParams()
  if (params.materialId) query.set('material_id', params.materialId)
  if (params.status && params.status !== 'all') query.set('status', params.status)
  if (params.source && params.source !== 'all') query.set('source', params.source)
  if (params.completed_lsx) query.set('completed_lsx', '1')
  if (params.planLineId) query.set('plan_line_id', params.planLineId)
  if (params.q?.trim()) query.set('q', params.q.trim())
  if (params.page != null) query.set('page', String(params.page))
  if (params.pageSize != null) query.set('pageSize', String(params.pageSize))

  const qs = query.toString()
  const res = await apiFetch(`/api/reservations${qs ? `?${qs}` : ''}`)
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: { message?: string } }
    throw new Error(err?.error?.message || `Lỗi tải danh sách giữ hàng (${res.status})`)
  }
  return res.json() as Promise<ReservationListResponse>
}

export async function createManualReservationApi(payload: {
  material_id: string
  qty_base: number
  plan_line_id?: string
  note?: string
}): Promise<ReservationItem> {
  const res = await apiFetch('/api/reservations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const body = (await res.json().catch(() => ({}))) as { ok?: boolean; data?: ReservationItem; error?: { message?: string } }
  if (!res.ok) {
    throw new Error(body?.error?.message || `Lỗi tạo giữ hàng (${res.status})`)
  }
  return body.data!
}

export async function updateReservationQtyApi(
  id: string,
  payload: { qty_base: number; reason: string },
): Promise<ReservationItem> {
  const res = await apiFetch(`/api/reservations/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const body = (await res.json().catch(() => ({}))) as { ok?: boolean; data?: ReservationItem; error?: { message?: string } }
  if (!res.ok) {
    throw new Error(body?.error?.message || `Lỗi sửa số lượng giữ (${res.status})`)
  }
  return body.data!
}

export async function releaseReservationApi(
  id: string,
  reason: string,
): Promise<ReservationItem> {
  const res = await apiFetch(`/api/reservations/${encodeURIComponent(id)}/release`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  })
  const body = (await res.json().catch(() => ({}))) as { ok?: boolean; data?: ReservationItem; error?: { message?: string } }
  if (!res.ok) {
    throw new Error(body?.error?.message || `Lỗi bỏ giữ (${res.status})`)
  }
  return body.data!
}
